// Flameco UI kit v2.8.0, copied by authservice ui-kit/sync.js. Do not edit; change the kit and re-sync.
'use client';
// Favourites and Recent for every Flameco app (UX Standard v2.1). Kept per
// person and per app: a local copy (instant, works offline) plus the person's
// Flameco account on the backend (GET/PUT /flameco/quick-access), so the same
// favourites follow them to another device. If the backend can't store them
// yet, the local copy keeps working on its own.
import { useCallback, useEffect, useState } from 'react';
import type { GlobalSearchConfig } from './GlobalSearch';

export interface QuickVisit {
  key: string;
  at: number;
}

export interface QuickState {
  favourites: string[];
  recent: QuickVisit[];
  /** ms since epoch of the last change; the newer copy (local or account) wins. */
  updatedAt: number;
}

/** Same shape as the search config: the backend base URL and the app's token. */
export type QuickAccessServer = Pick<GlobalSearchConfig, 'apiBase' | 'getToken'>;

const MAX_RECENT = 8;
/** Opening the same module again within this window only refreshes its time locally. */
const SAME_VISIT_MS = 60_000;

const empty = (): QuickState => ({ favourites: [], recent: [], updatedAt: 0 });
const storeKey = (app: string) => `fl_quick:${app}`;
const states = new Map<string, QuickState>();
const listeners = new Map<string, Set<(s: QuickState) => void>>();
const synced = new Set<string>();
const saveTimers = new Map<string, ReturnType<typeof setTimeout>>();

function readLocal(app: string): QuickState {
  try {
    const raw = localStorage.getItem(storeKey(app));
    if (!raw) return empty();
    const v = JSON.parse(raw);
    return {
      favourites: Array.isArray(v.favourites) ? v.favourites.filter((k: unknown) => typeof k === 'string') : [],
      recent: Array.isArray(v.recent)
        ? v.recent.filter((r: QuickVisit) => r && typeof r.key === 'string' && typeof r.at === 'number')
        : [],
      updatedAt: typeof v.updatedAt === 'number' ? v.updatedAt : 0,
    };
  } catch {
    return empty();
  }
}

function get(app: string): QuickState {
  let s = states.get(app);
  if (!s) {
    s = typeof window === 'undefined' ? empty() : readLocal(app);
    states.set(app, s);
  }
  return s;
}

function set(app: string, next: QuickState, server?: QuickAccessServer, push = true) {
  states.set(app, next);
  try {
    localStorage.setItem(storeKey(app), JSON.stringify(next));
  } catch {
    /* private mode: memory only */
  }
  listeners.get(app)?.forEach((fn) => fn(next));
  if (push && server) scheduleSave(app, server);
}

function url(server: QuickAccessServer, app: string) {
  return `${server.apiBase.replace(/\/+$/, '')}/flameco/quick-access?app=${encodeURIComponent(app)}`;
}

function scheduleSave(app: string, server: QuickAccessServer) {
  clearTimeout(saveTimers.get(app));
  saveTimers.set(
    app,
    setTimeout(() => {
      const token = server.getToken();
      if (!token) return;
      const s = get(app);
      fetch(url(server, app), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ favourites: s.favourites, recent: s.recent, updatedAt: s.updatedAt }),
      }).catch(() => {});
    }, 1200),
  );
}

/** Once per page load: take the account's copy if it is newer, else send ours up. */
async function syncOnce(app: string, server: QuickAccessServer) {
  if (synced.has(app)) return;
  const token = server.getToken();
  if (!token) return;
  synced.add(app);
  try {
    const res = await fetch(url(server, app), { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return; // not stored on the account yet: the local copy stands
    const remote = (await res.json()) as Partial<QuickState> | null;
    const local = get(app);
    if (remote && typeof remote.updatedAt === 'number' && remote.updatedAt > local.updatedAt) {
      set(
        app,
        {
          favourites: Array.isArray(remote.favourites) ? remote.favourites : [],
          recent: Array.isArray(remote.recent) ? remote.recent.slice(0, MAX_RECENT) : [],
          updatedAt: remote.updatedAt,
        },
        server,
        false,
      );
    } else if (local.updatedAt > (remote?.updatedAt ?? 0)) {
      scheduleSave(app, server);
    }
  } catch {
    synced.delete(app); // offline: try again next time a page mounts
  }
}

export interface QuickAccessApi extends QuickState {
  isFavourite: (key: string) => boolean;
  toggleFavourite: (key: string) => void;
  /** Record that a module was opened (most recent first, at most 8 kept). */
  visit: (key: string) => void;
}

/**
 * Favourites and Recent for `app` (e.g. 'mfg'). Pass the same `server` as the
 * frame's search config to keep them on the person's account.
 */
export function useQuickAccess(app: string, server?: QuickAccessServer | null): QuickAccessApi {
  const [state, setState] = useState<QuickState>(() => get(app));
  const srv = server ?? undefined;
  const signedIn = !!srv?.getToken();

  useEffect(() => {
    setState(get(app));
    let subs = listeners.get(app);
    if (!subs) listeners.set(app, (subs = new Set()));
    subs.add(setState);
    // Another tab changed them.
    const onStorage = (e: StorageEvent) => {
      if (e.key === storeKey(app)) {
        const next = readLocal(app);
        states.set(app, next);
        listeners.get(app)?.forEach((fn) => fn(next));
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      subs!.delete(setState);
      window.removeEventListener('storage', onStorage);
    };
  }, [app]);

  useEffect(() => {
    if (srv && signedIn) void syncOnce(app, srv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app, signedIn]);

  const toggleFavourite = useCallback(
    (key: string) => {
      const s = get(app);
      const favourites = s.favourites.includes(key) ? s.favourites.filter((k) => k !== key) : [...s.favourites, key];
      set(app, { ...s, favourites, updatedAt: Date.now() }, srv);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [app, srv?.apiBase],
  );

  const visit = useCallback(
    (key: string) => {
      const s = get(app);
      const now = Date.now();
      const top = s.recent[0];
      if (top && top.key === key && now - top.at < SAME_VISIT_MS) return;
      const recent = [{ key, at: now }, ...s.recent.filter((r) => r.key !== key)].slice(0, MAX_RECENT);
      set(app, { ...s, recent, updatedAt: now }, srv);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [app, srv?.apiBase],
  );

  return {
    ...state,
    isFavourite: (key: string) => state.favourites.includes(key),
    toggleFavourite,
    visit,
  };
}

/**
 * The module a path belongs to (longest matching href wins), for recording
 * visits from the router: `const m = moduleForPath(pathname, modules); if (m) visit(m.key)`.
 */
export function moduleForPath<T extends { key: string; href?: string }>(path: string, modules: T[]): T | null {
  let best: T | null = null;
  let bestLen = -1;
  for (const m of modules) {
    if (!m.href) continue;
    const href = m.href.split(/[?#]/)[0].replace(/\/+$/, '');
    if (!href || href === '/') continue;
    if ((path === href || path.startsWith(href + '/')) && href.length > bestLen) {
      best = m;
      bestLen = href.length;
    }
  }
  return best;
}
